import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Activity, Beaker, Dumbbell, HeartPulse, Moon, Save, Sparkles,
  TrendingUp, TrendingDown, Minus, Clock,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  BIOMETRIC_BODY_FIELDS,
  BIOMETRIC_BLOOD_FIELDS,
  BIOMETRIC_VO2_FIELD,
  BIOMETRIC_SLEEP_FIELD,
  PHASE_LABELS,
  buildBiometricPayload,
  biometricPayloadHasData,
  describeBiologicalAge,
  emptyBiometricForm,
  extractBiometricSeries,
  groupBiometricEntriesByPhase,
  longDateTime,
  shortDate,
} from "@/lib/LongevityEngine";
import { HEALTH_EXTRA_FIELDS } from "./healthMetricFields";

/**
 * BiometricVault — shared view for the Longevity Biometric Vault.
 *
 * Props:
 *  - entries:       array of biometric entries (newest first).
 *  - loading:       boolean while fetching.
 *  - onSave(payload): async handler that POSTs a new entry.
 *  - mode: "guest" | "staff" — controls blood/entry capabilities.
 *
 * Guest mode: Body Composition / VO2 Max / Sleep Score are editable.
 * Blood Results + Biological Age are read-only (display cards).
 *
 * Staff mode: everything above + Blood Results are editable.
 *
 * Every save creates a new timestamped record (append-only, server-side).
 */

const NUMBER_FIELD_PROPS = { inputMode: "decimal", placeholder: "—" };

function FieldInput({ field, value, onChange, testId, disabled }) {
  return (
    <div>
      <Label className="text-[11px] text-muted-foreground">
        {field.label}{field.unit ? ` (${field.unit})` : ""}
      </Label>
      <Input
        {...NUMBER_FIELD_PROPS}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        disabled={disabled}
        className="mt-1"
      />
    </div>
  );
}

function MetricTile({ label, value, unit, testId }) {
  const display = value === null || value === undefined || value === "" ? "—" : value;
  return (
    <div className="rounded-lg border border-border bg-background p-3" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold mt-0.5">
        {display}{unit && display !== "—" ? <span className="text-xs text-muted-foreground ml-1">{unit}</span> : null}
      </p>
    </div>
  );
}

function TrendBadge({ series }) {
  if (!series || series.length < 2) return null;
  const first = series[0].value;
  const last = series[series.length - 1].value;
  const delta = last - first;
  if (Math.abs(delta) < 0.05) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
        <Minus className="w-3 h-3" /> stable
      </span>
    );
  }
  const up = delta > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] ${up ? "text-emerald-700" : "text-rose-700"}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? "+" : ""}{delta.toFixed(1)}
    </span>
  );
}

function MiniTrendChart({ series, color = "#0f766e", testId }) {
  if (!series || series.length < 2) return null;
  const data = series.map((p) => ({ ...p, label: shortDate(p.date) }));
  return (
    <div className="h-24 mt-2" data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 6, left: -16, bottom: 0 }}>
          <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} domain={["auto", "auto"]} />
          <Tooltip
            labelFormatter={(l, items) => (items?.[0]?.payload?.date ? longDateTime(items[0].payload.date) : l)}
            contentStyle={{ fontSize: 11, borderRadius: 8 }}
          />
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={{ r: 2 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function CardShell({ icon: Icon, title, badge, children, testId }) {
  return (
    <Card className="rounded-2xl" data-testid={testId}>
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-primary" />
            <p className="text-sm font-medium">{title}</p>
          </div>
          {badge}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

export default function BiometricVault({ entries, loading, onSave, mode = "guest" }) {
  const isStaff = mode === "staff";
  const [form, setForm] = useState(emptyBiometricForm());
  const [saving, setSaving] = useState(false);

  const setBody = (key, v) => setForm((f) => ({ ...f, body_composition: { ...f.body_composition, [key]: v } }));
  const setBlood = (key, v) => setForm((f) => ({ ...f, blood_results: { ...f.blood_results, [key]: v } }));
  const setSingle = (key, v) => setForm((f) => ({ ...f, [key]: v }));

  const handleSave = useCallback(async () => {
    const payload = buildBiometricPayload(form, { includeBlood: isStaff });
    if (!biometricPayloadHasData(payload)) {
      toast.error("Enter at least one measurement before saving.");
      return;
    }
    setSaving(true);
    try {
      await onSave(payload);
      toast.success("New biometric record saved.");
      setForm(emptyBiometricForm());
    } catch (e) {
      toast.error(e?.message || "Could not save your entry.");
    } finally {
      setSaving(false);
    }
  }, [form, isStaff, onSave]);

  const latest = entries?.[0] || null;
  const phaseGroups = useMemo(() => groupBiometricEntriesByPhase(entries || []), [entries]);

  const bodyWeightSeries = useMemo(() => extractBiometricSeries(entries, "body_composition.weight_kg"), [entries]);
  const bodyFatSeries = useMemo(() => extractBiometricSeries(entries, "body_composition.body_fat_percent"), [entries]);
  const muscleSeries = useMemo(() => extractBiometricSeries(entries, "body_composition.muscle_mass_kg"), [entries]);
  const visceralSeries = useMemo(() => extractBiometricSeries(entries, "body_composition.visceral_fat"), [entries]);
  const vo2Series = useMemo(() => extractBiometricSeries(entries, "vo2_max"), [entries]);
  const sleepSeries = useMemo(() => extractBiometricSeries(entries, "sleep_score"), [entries]);
  const restingHrSeries = useMemo(() => extractBiometricSeries(entries, "resting_heart_rate"), [entries]);
  const stepsSeries = useMemo(() => extractBiometricSeries(entries, "daily_steps"), [entries]);
  const bioAgeSeries = useMemo(() => extractBiometricSeries(entries, "biological_age"), [entries]);
  const bloodSeries = useMemo(() => {
    const map = {};
    for (const f of BIOMETRIC_BLOOD_FIELDS) {
      map[f.key] = extractBiometricSeries(entries, `blood_results.${f.key}`);
    }
    return map;
  }, [entries]);

  const bioAgeSentence = latest ? describeBiologicalAge(latest) : null;

  return (
    <div className="space-y-5" data-testid="biometric-vault-root">
      {/* Body Composition */}
      <CardShell
        icon={Dumbbell}
        title="Body Composition"
        testId="biometric-card-body"
        badge={bodyWeightSeries.length >= 2 && <TrendBadge series={bodyWeightSeries} />}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          {BIOMETRIC_BODY_FIELDS.map((f) => (
            <MetricTile
              key={f.key}
              label={`Latest ${f.label}`}
              value={latest?.body_composition?.[f.key] ?? null}
              unit={f.unit}
              testId={`biometric-latest-${f.key}`}
            />
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {BIOMETRIC_BODY_FIELDS.map((f) => (
            <FieldInput
              key={f.key}
              field={f}
              value={form.body_composition[f.key]}
              onChange={(v) => setBody(f.key, v)}
              testId={`biometric-input-${f.key}`}
            />
          ))}
        </div>
        {(bodyWeightSeries.length >= 2 || bodyFatSeries.length >= 2 || muscleSeries.length >= 2 || visceralSeries.length >= 2) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            {bodyWeightSeries.length >= 2 && (
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Weight trend</p>
                <MiniTrendChart series={bodyWeightSeries} testId="biometric-chart-weight_kg" /></div>
            )}
            {bodyFatSeries.length >= 2 && (
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Body fat trend</p>
                <MiniTrendChart series={bodyFatSeries} color="#b45309" testId="biometric-chart-body_fat_percent" /></div>
            )}
            {muscleSeries.length >= 2 && (
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Muscle mass trend</p>
                <MiniTrendChart series={muscleSeries} color="#1d4ed8" testId="biometric-chart-muscle_mass_kg" /></div>
            )}
            {visceralSeries.length >= 2 && (
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Visceral fat trend</p>
                <MiniTrendChart series={visceralSeries} color="#7c3aed" testId="biometric-chart-visceral_fat" /></div>
            )}
          </div>
        )}
      </CardShell>

      {/* Blood Results — read-only for guests, editable for staff */}
      <CardShell
        icon={Beaker}
        title="Blood Results"
        testId="biometric-card-blood"
        badge={!isStaff && <Badge variant="outline" className="text-[10px]" data-testid="biometric-blood-readonly-badge">Staff-entered</Badge>}
      >
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
          {BIOMETRIC_BLOOD_FIELDS.map((f) => (
            <MetricTile
              key={f.key}
              label={`Latest ${f.label}`}
              value={latest?.blood_results?.[f.key] ?? null}
              unit={f.unit}
              testId={`biometric-latest-${f.key}`}
            />
          ))}
        </div>
        {isStaff ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {BIOMETRIC_BLOOD_FIELDS.map((f) => (
              <FieldInput
                key={f.key}
                field={f}
                value={form.blood_results[f.key]}
                onChange={(v) => setBlood(f.key, v)}
                testId={`biometric-input-${f.key}`}
              />
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground italic">
            Blood results are added by your Cocoon care team during your visit.
          </p>
        )}
        {isStaff && Object.values(bloodSeries).some((s) => s.length >= 2) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            {BIOMETRIC_BLOOD_FIELDS.map((f) =>
              (bloodSeries[f.key] || []).length >= 2 ? (
                <div key={f.key}>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{f.label} trend</p>
                  <MiniTrendChart series={bloodSeries[f.key]} color="#9f1239" testId={`biometric-chart-${f.key}`} />
                </div>
              ) : null,
            )}
          </div>
        )}
      </CardShell>

      {/* VO2 Max */}
      <CardShell
        icon={HeartPulse}
        title="VO2 Max"
        testId="biometric-card-vo2"
        badge={vo2Series.length >= 2 && <TrendBadge series={vo2Series} />}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <MetricTile label="Latest VO2 Max" value={latest?.vo2_max ?? null} unit={BIOMETRIC_VO2_FIELD.unit} testId="biometric-latest-vo2_max" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <FieldInput
            field={BIOMETRIC_VO2_FIELD}
            value={form.vo2_max}
            onChange={(v) => setSingle("vo2_max", v)}
            testId="biometric-input-vo2_max"
          />
        </div>
        {vo2Series.length >= 2 && <MiniTrendChart series={vo2Series} color="#be123c" testId="biometric-chart-vo2_max" />}
      </CardShell>

      {/* Sleep Score */}
      <CardShell
        icon={Moon}
        title="Sleep Score"
        testId="biometric-card-sleep"
        badge={sleepSeries.length >= 2 && <TrendBadge series={sleepSeries} />}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <MetricTile label="Latest Sleep Score" value={latest?.sleep_score ?? null} unit={BIOMETRIC_SLEEP_FIELD.unit} testId="biometric-latest-sleep_score" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <FieldInput
            field={BIOMETRIC_SLEEP_FIELD}
            value={form.sleep_score}
            onChange={(v) => setSingle("sleep_score", v)}
            testId="biometric-input-sleep_score"
          />
        </div>
        {sleepSeries.length >= 2 && <MiniTrendChart series={sleepSeries} color="#4338ca" testId="biometric-chart-sleep_score" />}
      </CardShell>

      {/* Activity & Heart — read-only; populated by Upload Health Screenshot */}
      <CardShell
        icon={Activity}
        title="Activity & Heart"
        testId="biometric-card-activity"
        badge={<Badge variant="outline" className="text-[10px]" data-testid="biometric-activity-readonly-badge">From your health app</Badge>}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {HEALTH_EXTRA_FIELDS.map((f) => (
            <MetricTile
              key={f.key}
              label={`Latest ${f.label}`}
              value={latest?.[f.key] ?? null}
              unit={f.unit}
              testId={`biometric-latest-${f.key}`}
            />
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground italic mt-3">
          These come from your health app — use Upload Health Report at the top of this page to add them.
        </p>
        {(restingHrSeries.length >= 2 || stepsSeries.length >= 2) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            {restingHrSeries.length >= 2 && (
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Resting heart rate trend</p>
                <MiniTrendChart series={restingHrSeries} color="#be123c" testId="biometric-chart-resting_heart_rate" /></div>
            )}
            {stepsSeries.length >= 2 && (
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Daily steps trend</p>
                <MiniTrendChart series={stepsSeries} color="#0f766e" testId="biometric-chart-daily_steps" /></div>
            )}
          </div>
        )}
      </CardShell>

      {/* Biological Age — read-only (auto-calculated on save) */}
      <CardShell
        icon={Sparkles}
        title="Biological Age"
        testId="biometric-card-bio-age"
        badge={<Badge variant="outline" className="text-[10px]" data-testid="biometric-bio-age-auto-badge">Auto-calculated</Badge>}
      >
        {latest?.biological_age != null ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-2">
              <MetricTile label="Biological Age" value={latest.biological_age} unit="yrs" testId="biometric-latest-biological_age" />
              <MetricTile label="Actual Age" value={latest.actual_age ?? "—"} unit="yrs" testId="biometric-latest-actual_age" />
              <MetricTile
                label="Δ vs actual"
                value={(latest.biological_age_delta ?? 0) > 0 ? `+${latest.biological_age_delta}` : latest.biological_age_delta}
                unit="yrs"
                testId="biometric-latest-biological_age_delta"
              />
            </div>
            {bioAgeSentence && (
              <p className="text-sm font-medium mt-2" data-testid="biometric-bio-age-sentence">{bioAgeSentence}</p>
            )}
            <p className="text-[11px] text-muted-foreground italic mt-1">
              Recalculated from Body Fat, VO2 Max, Sleep Score, HbA1c and Fasting Glucose on each new entry.
            </p>
            {bioAgeSeries.length >= 2 && <MiniTrendChart series={bioAgeSeries} color="#0e7490" testId="biometric-chart-biological_age" />}
          </>
        ) : (
          <p className="text-[11px] text-muted-foreground italic">
            Your Biological Age appears here once we have your date of birth and at least one measurement.
          </p>
        )}
      </CardShell>

      {/* Save bar */}
      <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-background/90 backdrop-blur-sm py-3 border-t">
        <p className="text-[11px] text-muted-foreground mr-auto">
          Each save adds a new timestamped record — your history is never overwritten.
        </p>
        <Button
          onClick={handleSave}
          disabled={saving || loading}
          data-testid="biometric-save-btn"
          className="rounded-full"
        >
          <Save className="w-4 h-4 mr-1.5" />
          {saving ? "Saving…" : "Save new entry"}
        </Button>
      </div>

      {/* Progress — timeline grouped by phase */}
      <div data-testid="biometric-progress-root">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold">Progress</h3>
          <span className="text-[11px] text-muted-foreground">(newest first)</span>
        </div>
        {loading && (
          <p className="text-xs text-muted-foreground py-4" data-testid="biometric-progress-loading">
            Loading your history…
          </p>
        )}
        {!loading && (entries || []).length === 0 && (
          <p className="text-xs text-muted-foreground py-4" data-testid="biometric-progress-empty">
            No records yet. Save your first entry above — it anchors your longitudinal journey.
          </p>
        )}
        {!loading && (entries || []).length > 0 && phaseGroups.map((group) => (
          group.entries.length > 0 ? (
            <div key={group.phase} className="mb-5" data-testid={`biometric-phase-${group.phase}`}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">{group.label} · {group.entries.length}</p>
              <div className="space-y-2">
                {group.entries.map((e) => (
                  <HistoryRow key={e.id} entry={e} />
                ))}
              </div>
            </div>
          ) : null
        ))}
      </div>
    </div>
  );
}

function HistoryRow({ entry }) {
  const actorKind = entry?.actor?.kind;
  const actorName = entry?.actor?.name || "—";
  return (
    <div
      className="rounded-lg border border-border p-3 bg-background text-xs"
      data-testid={`biometric-history-row-${entry.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-medium">{longDateTime(entry.recorded_at)}</p>
          <p className="text-[11px] text-muted-foreground">
            {actorKind === "guest" ? "Self-entry" : "Staff"} · {actorName}
          </p>
        </div>
        <Badge variant="outline" className="text-[10px]" data-testid={`biometric-history-phase-${entry.id}`}>
          {PHASE_LABELS[entry.phase] || "—"}
        </Badge>
      </div>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {Object.keys(entry.body_composition || {}).length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Body</p>
            <ul className="space-y-0.5">
              {BIOMETRIC_BODY_FIELDS.map((f) => (
                entry.body_composition?.[f.key] != null ? (
                  <li key={f.key}><b>{f.label}:</b> {entry.body_composition[f.key]} {f.unit}</li>
                ) : null
              ))}
            </ul>
          </div>
        )}
        {Object.keys(entry.blood_results || {}).length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Blood</p>
            <ul className="space-y-0.5">
              {BIOMETRIC_BLOOD_FIELDS.map((f) => (
                entry.blood_results?.[f.key] != null ? (
                  <li key={f.key}><b>{f.label}:</b> {entry.blood_results[f.key]} {f.unit}</li>
                ) : null
              ))}
            </ul>
          </div>
        )}
        {(entry.vo2_max != null || entry.sleep_score != null || entry.biological_age != null) && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Performance</p>
            <ul className="space-y-0.5">
              {entry.vo2_max != null && <li><b>VO2 Max:</b> {entry.vo2_max} ml/kg/min</li>}
              {entry.sleep_score != null && <li><b>Sleep Score:</b> {entry.sleep_score}/100</li>}
              {entry.biological_age != null && (
                <li><b>Biological Age:</b> {entry.biological_age} yrs
                  {entry.biological_age_delta != null && (
                    <span className="text-muted-foreground"> ({entry.biological_age_delta > 0 ? "+" : ""}{entry.biological_age_delta} vs actual)</span>
                  )}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
